<template>
  <div class="co-page">
    <header class="co-hero">
      <div>
        <div class="zh-eyebrow"><span class="zh-dot"></span> Administration</div>
        <h1 class="zh-title">Partner <span>companies</span></h1>
        <p class="zh-sub">Every company with its users and the trees assigned to it. A company in red has fewer splits available than its minimum.</p>
      </div>
    </header>

    <div v-if="notice" class="co-alert ok"><i class="fas fa-check-circle"></i> {{ notice }}</div>
    <div v-if="error" class="co-alert bad"><i class="fas fa-exclamation-triangle"></i> {{ error }}</div>

    <div v-if="tab === 'active'" class="co-tiles">
      <div class="co-tile"><span>Companies</span><b>{{ fmt(totals.companies) }}</b></div>
      <div class="co-tile"><span>Users</span><b>{{ fmt(totals.users) }}</b><small>{{ fmt(totals.activeUsers) }} active</small></div>
      <div class="co-tile"><span>Assigned trees</span><b>{{ fmt(totals.trees) }}</b></div>
      <div class="co-tile"><span>Splits available</span><b>{{ fmt(totals.splits) }}</b></div>
      <div class="co-tile" :class="{ low: totals.below }"><span>Below their minimum</span><b>{{ fmt(totals.below) }}</b></div>
    </div>

    <div class="co-bar">
      <div class="co-tabs" role="tablist">
        <button type="button" role="tab" :class="{ on: tab === 'active' }" @click="setTab('active')">Active</button>
        <button type="button" role="tab" :class="{ on: tab === 'archived' }" @click="setTab('archived')">Archived</button>
      </div>
      <div class="co-search"><i class="fas fa-search"></i><input v-model="q" type="search" placeholder="Search a company…"></div>
    </div>

    <div class="co-card">
      <div class="table-responsive">
        <table class="co-table">
          <thead>
            <tr>
              <th>Company</th>
              <th class="num">Users</th>
              <th class="num">Assigned trees</th>
              <th class="num">Trees with stock</th>
              <th class="num">Splits available</th>
              <th class="num">Minimum</th>
              <th class="act">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in shown" :key="c.id" :class="{ low: c.belowMinimum }">
              <td>
                <router-link :to="`/companies/${c.id}`" class="co-name"><i class="fas fa-building"></i> {{ c.name }}</router-link>
                <span v-if="c.belowMinimum" class="co-tag low" title="Fewer splits than the minimum">Low stock</span>
                <div v-if="c.archived && c.archivedAt" class="co-muted">Archived {{ niceDate(c.archivedAt) }}</div>
              </td>
              <td class="num">{{ c.users }}<span v-if="c.users && c.activeUsers !== c.users" class="co-muted"> ({{ c.activeUsers }} active)</span></td>
              <td class="num strong">{{ fmt(c.assignedTrees) }}</td>
              <td class="num">{{ fmt(c.treesWithStock) }}</td>
              <td class="num strong" :class="{ 'text-danger': c.belowMinimum }">{{ fmt(c.splitsAvailable) }}</td>
              <td class="num">{{ c.minAvailableSplits ? fmt(c.minAvailableSplits) : '—' }}</td>
              <td class="act">
                <template v-if="!c.archived">
                  <router-link :to="`/companies/${c.id}`" class="co-btn ghost"><i class="fas fa-users"></i> Details</router-link>
                  <button type="button" class="co-btn primary" @click="open('assign', c)"><i class="fas fa-plus-circle"></i> Assign trees</button>
                  <button type="button" class="co-btn icon danger" title="Archive" aria-label="Archive" @click="open('archive', c)"><i class="fas fa-archive"></i></button>
                </template>
                <template v-else>
                  <router-link :to="`/companies/${c.id}`" class="co-btn ghost">View</router-link>
                  <button type="button" class="co-btn primary" @click="open('restore', c)"><i class="fas fa-undo"></i> Restore</button>
                  <button type="button" class="co-btn danger" @click="open('delete', c)"><i class="fas fa-trash-alt"></i> Delete</button>
                </template>
              </td>
            </tr>
            <tr v-if="!shown.length">
              <td colspan="7" class="co-empty">
                {{ loading ? 'Loading…' : q ? 'No company matches.' : tab === 'active' ? 'No companies yet.' : 'No archived companies.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <trees-dialog v-if="dialog && dialog.kind === 'assign'" mode="assign" :company="dialog.company" @close="dialog = null" @done="finished"/>
    <company-action-dialog v-else-if="dialog" :mode="dialog.kind" :company="dialog.company" @close="dialog = null" @done="finished"/>
  </div>
</template>

<script>
import Companies, { apiError } from '../api/Companies';
import TreesDialog from '../components/Companies/TreesDialog.vue';
import CompanyActionDialog from '../components/Companies/CompanyActionDialog.vue';
import adminOnlyPage from '../mixins/adminOnlyPage';

/** Administrators: every partner company with its users and the trees assigned to it (same as the panel's Companies). */
export default {
  name: 'Companies',
  components: { TreesDialog, CompanyActionDialog },
  mixins: [adminOnlyPage],
  data() {
    return { tab: 'active', companies: [], loading: true, error: '', notice: '', q: '', dialog: null };
  },
  computed: {
    shown() {
      const q = this.q.trim().toLowerCase();
      return q ? this.companies.filter((c) => String(c.name).toLowerCase().includes(q)) : this.companies;
    },
    totals() {
      const list = this.companies;
      return {
        companies: list.length,
        users: list.reduce((n, c) => n + (c.users || 0), 0),
        activeUsers: list.reduce((n, c) => n + (c.activeUsers || 0), 0),
        trees: list.reduce((n, c) => n + (c.assignedTrees || 0), 0),
        splits: list.reduce((n, c) => n + (c.splitsAvailable || 0), 0),
        below: list.filter((c) => c.belowMinimum).length,
      };
    },
  },
  created() { this.load(); },
  methods: {
    fmt(v) { return new Intl.NumberFormat('en-GB').format(Number(v) || 0); },
    niceDate(d) { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); },
    async load(spinner = true) {
      if (spinner) this.loading = true;
      try {
        this.companies = (await Companies.list(this.tab === 'archived')).data || [];
        this.error = '';
      } catch (err) {
        this.error = apiError(err, 'Could not load the companies.');
      } finally {
        this.loading = false;
      }
    },
    setTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.companies = [];
      this.notice = '';
      this.load();
    },
    open(kind, company) { this.notice = ''; this.dialog = { kind, company }; },
    finished(message) {
      this.dialog = null;
      this.notice = message;
      this.load(false);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../components/Stats/intro.scss';
@import '../components/Companies/companies.scss';
</style>
